/**
 * DashboardPage - main dashboard view with stats and activity
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@stores/authStore';
import { useDashboardStore } from '@stores/dashboardStore';
import {
  StatsCard,
  ActivityFeed,
  TasksDueSoon,
  ProjectsList,
  MyTasks,
  UpcomingMeetings,
  TodaysFocus,
  FirstRunHero,
} from '@components/dashboard';

export default function DashboardPage() {
  const { t } = useTranslation();
  const { user, currentOrgId } = useAuthStore();
  const {
    stats,
    recentActivity,
    tasksDueSoon,
    myTasks,
    projects,
    upcomingMeetings,
    isLoading,
    error,
    fetchDashboard,
    clearError,
  } = useDashboardStore();

  useEffect(() => {
    if (currentOrgId) {
      fetchDashboard(currentOrgId);
    }
  }, [currentOrgId, fetchDashboard]);

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-50 p-8 dark:bg-neutral-900">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-red-800 dark:text-red-200">{error}</p>
            <button
              onClick={clearError}
              className="mt-2 text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
            >
              {t('dashboard.dismiss')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
            {t('dashboard.title')}
          </h1>
          <p className="mt-1 text-neutral-600 dark:text-neutral-400">
            {t('dashboard.welcome', { name: user?.displayName ?? 'User' })}
          </p>
        </div>

        {/* First run: no project and no task yet -> single primary action */}
        {!isLoading && projects.length === 0 && (stats?.totalTasks ?? 0) === 0 ? (
          <FirstRunHero />
        ) : (
        <>
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatsCard
            title={t('dashboard.totalTasks')}
            value={stats?.totalTasks ?? 0}
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                />
              </svg>
            }
            color="blue"
          />
          <StatsCard
            title={t('dashboard.completed')}
            value={stats?.completedTasks ?? 0}
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            }
            color="green"
          />
          <StatsCard
            title={t('dashboard.inProgress')}
            value={stats?.inProgressTasks ?? 0}
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            }
            color="yellow"
          />
          <StatsCard
            title={t('dashboard.overdue')}
            value={stats?.overdueTasks ?? 0}
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            }
            color="red"
          />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Activity & Tasks */}
          <div className="lg:col-span-2 space-y-8">
            {/* Today's Focus - AI-powered recommendations */}
            {currentOrgId && <TodaysFocus organizationId={currentOrgId} />}
            <ActivityFeed activities={recentActivity} isLoading={isLoading} />
            <MyTasks tasks={myTasks} isLoading={isLoading} />
          </div>

          {/* Right Column - Due Soon, Projects, Meetings */}
          <div className="space-y-8">
            <TasksDueSoon tasks={tasksDueSoon} isLoading={isLoading} />
            <ProjectsList projects={projects} isLoading={isLoading} />
            <UpcomingMeetings meetings={upcomingMeetings} isLoading={isLoading} />
          </div>
        </div>

        {/* Completion Rate Card */}
        {stats && (
          <div className="mt-8 bg-white dark:bg-neutral-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-neutral-900 dark:text-white">{t('dashboard.overallProgress')}</h3>
              <span className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                {Math.round(stats.completionRate)}%
              </span>
            </div>
            <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-4">
              <div
                className="bg-primary-600 h-4 rounded-full transition-all duration-500"
                style={{ width: `${stats.completionRate}%` }}
              />
            </div>
            <div className="mt-4 grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-neutral-900 dark:text-white">{stats.activeProjects}</p>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">{t('dashboard.activeProjects')}</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-neutral-900 dark:text-white">{stats.totalMeetings}</p>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">{t('dashboard.totalMeetings')}</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-neutral-900 dark:text-white">{stats.upcomingMeetings}</p>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">{t('dashboard.upcomingMeetings')}</p>
              </div>
            </div>
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}
